import React from "react";
import { motion } from "framer-motion";
import { Gamepad2, Car, Bike } from "lucide-react";

const ASSETS = {
  pubg: "https://customer-assets.emergentagent.com/job_creator-website-1/artifacts/5h9pa7tq_pubg%20Arsov%20logoo.jpeg",
  efootball: "https://customer-assets.emergentagent.com/job_creator-website-1/artifacts/duq3hqbp_eFootball%20ARSOV.png",
  scriptLogo: "https://customer-assets.emergentagent.com/job_creator-website-1/artifacts/2tjfpuko_ARSOV%20A%202.jpg",
  navyLogo: "https://customer-assets.emergentagent.com/job_creator-website-1/artifacts/1lqtao6e_A%20logo.png",
  pov: "https://images.unsplash.com/photo-1761473573747-91840566ef1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  mtb: "https://images.unsplash.com/photo-1671423077966-f915e39c971c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const SeriesCard = ({ img, label, title, desc, icon: Icon, accent }) => (
  <div
    data-testid={`series-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className="group relative overflow-hidden border border-white/5 bg-[#0A0A0A] aspect-[4/5]"
  >
    <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
    <div className="absolute top-5 left-5 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/80 bg-black/50 px-2.5 py-1 border border-white/10">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-6">
      <h4 className="font-display text-3xl md:text-4xl uppercase leading-tight">{title}</h4>
      <p className="mt-2 text-sm text-white/70 max-w-xs">{desc}</p>
    </div>
  </div>
);

export default function BrandGallery({ stats }) {
  const description = (stats?.description || "").trim();
  // Split the description into paragraphs by double newlines, falling back to a default copy if empty.
  const paragraphs = description
    ? description.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    : [
        "ARSOV is a creator hub built around three obsessions: high-stakes gaming sessions, late-night POV drives, and unfiltered mountain bike runs. Every upload is graded, scored, and edited like a short film.",
      ];

  return (
    <section
      id="brand"
      data-testid="brand-section"
      className="relative py-24 md:py-36 bg-[#050505] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" id="about">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C41E3A] mb-4 flex items-center gap-3">
              <span className="w-10 h-px bg-[#C41E3A]" /> The Brand
            </p>
            <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">
              One name.<br />
              <span className="text-[#C41E3A]">Three lanes.</span>
            </h2>
          </div>
          <div
            className="lg:col-span-6 lg:col-start-7 text-white/70 text-base md:text-lg leading-relaxed space-y-5 whitespace-pre-line"
            data-testid="brand-description"
          >
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>

        {/* Logo showcase strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16"
        >
          <div className="relative aspect-[16/9] bg-black border border-white/5 overflow-hidden flex items-center justify-center group">
            <img src={ASSETS.scriptLogo} alt="ARSOV script logo" className="max-h-full w-auto object-contain p-8 transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute bottom-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/60">
              01 / Wordmark
            </div>
          </div>
          <div className="relative aspect-[16/9] bg-[#0F1A3C] border border-white/5 overflow-hidden flex items-center justify-center group">
            <img src={ASSETS.navyLogo} alt="ARSOV A monogram" className="max-h-full w-auto object-contain p-8 transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute bottom-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/60">
              02 / Monogram
            </div>
          </div>
        </motion.div>

        {/* Three lanes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SeriesCard
            img={ASSETS.pubg}
            label="Gaming"
            title="Battlegrounds"
            desc="Tactical drops, clutch finishes, and ranked grinds across PUBG and eFootball."
            icon={Gamepad2}
            accent="#C41E3A"
          />
          <SeriesCard
            img={ASSETS.pov}
            label="POV"
            title="Night Drives"
            desc="First-person rides through neon streets, mountain switchbacks, and open roads."
            icon={Car}
            accent="#C41E3A"
          />
          <SeriesCard
            img={ASSETS.mtb}
            label="MTB"
            title="Trail Runs"
            desc="Helmet-cam descents and dirt-line edits cut to the rhythm of the trail."
            icon={Bike}
            accent="#C41E3A"
          />
        </div>
      </div>
    </section>
  );
}
